/**
 * The gate on `/dashboard/*` and `/account`.
 *
 * Three states, and the middle one is the whole reason this is a component
 * rather than a one-line check: while the session is still being fetched we
 * render nothing at all. Rendering the login form first and swapping it out a
 * moment later is how a gated SPA announces that it is broken, and it happens
 * on every reload if "no user yet" is confused with "no user".
 *
 * This is a client-side convenience, not a security boundary. The worker
 * refuses unauthenticated requests on its own; nothing here is trusted to keep
 * anyone out of anything.
 */

import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useSession } from "./AuthProvider";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, checking } = useSession();
  const location = useLocation();

  if (checking) return null;

  if (!user) {
    // Where they were going travels with the redirect, so signing in returns
    // them there instead of dumping them on the dashboard root. `replace`
    // keeps the gated URL out of history — pressing Back from the login form
    // should leave, not bounce off the gate again.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
